import React, { useEffect, useState } from 'react';
import {
	Drawer,
	Form,
	Space,
	Switch,
	Table,
	Typography,
} from 'antd';
import { Currencies } from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatNumber } from '@shared/utils/formatNumber';
import { asyncMap } from '@shared/utils/array';
import { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import { FuelEuRow } from '@api/features/ops/utils/emissionsCompliance/emissionsComplianceTypes';
import Button from '@client/components/Button';
import { updateVoyageExpense } from '@client/lib/api';
import useFileUpload from '@client/utils/hooks/useFileUpload';
import getFileFromAttachment from '@client/utils/getFileFromAttachment';
import LoadingIndicator from '@client/components/LoadingIndicator';
import NumericInput from '@client/components/NumericInput';
import EstimatedActualTag from '@client/components/EstimatedActualTag';
import CurrencyInput from '@client/components/CurrencyInput';
import InvoiceAttachment from '../InvoiceAttachment';
import styles from './FuelEuExpenseDrawer.module.css';

type Props = {
	open: boolean;
	onClose: () => void;
	expenseData?: Partial<GetVoyageExpensesResponse['rows'][number]> | null;
};

type SavedSnapshot = {
	overrideAmount: number | null;
	fuelEuExchangeRate: number | null;
	rows: FuelEuRow[];
	attachments: Array<{
		name: string;
		attachmentId?: number;
	}>;
};

type UploadedAttachmentFile = File & {
	uid: string;
	attachmentId?: number;
};

const serializeSnapshot = (snapshot: SavedSnapshot) => JSON.stringify(snapshot);

const renderConsumptionSource = (
	source?: 'actual' | 'estimated',
) => {
	if (source == null) {
		return '—';
	}

	return (
		<EstimatedActualTag
			estimated={source === 'estimated'}
		/>
	);
};

const FuelEuExpenseDrawer = ({
	open,
	onClose,
	expenseData,
}: Props) => {
	const hasBreakdown = (expenseData?.fuelEuCalculationRows?.length ?? 0) > 0;
	const [saving, setSaving] = useState(false);
	const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
	const [fuelEuExchangeRate, setFuelEuExchangeRate] = useState<number | null>(
		expenseData?.fuelEuExchangeRate ?? null,
	);
	const [localRows, setLocalRows] = useState<FuelEuRow[]>([]);
	const [loadingAttachments, setLoadingAttachments] = useState(false);
	const [overrideAmount, setOverrideAmount] = useState<number | null>(
		expenseData?.overrideAmount == null ?
			null :
			Number(expenseData.overrideAmount),
	);

	const {
		uploaded,
		uploadProps,
		onRemoveUpload,
		onSetUploaded,
		onResetUploaded,
	} = useFileUpload({ multiple: true });

	const currency = expenseData?.currency ?? Currencies.USD;
	const isEurContract = currency === Currencies.EUR;
	const hasResolvableFx =
		isEurContract ||
		(
			fuelEuExchangeRate != null &&
			Number.isFinite(fuelEuExchangeRate) &&
			fuelEuExchangeRate > 0
		);
	const fxToUse = isEurContract ? 1 : (fuelEuExchangeRate ?? 1);
	const calculatedRows = localRows.map((row) => ({
		...row,
		contractAmount:
		(row.apply ? row.localAmount : 0) *
		fxToUse,
	}));

	const totalConsumption = calculatedRows.reduce(
		(sum, row) => sum + (row.apply ? row.consumption : 0),
		0,
	);

	const totalLocalAmount = calculatedRows.reduce(
		(sum, row) => sum + (row.apply ? row.localAmount : 0),
		0,
	);

	const totalContractAmount = calculatedRows.reduce(
		(sum, row) => sum + row.contractAmount,
		0,
	);

	useEffect(() => {
		const initialFx =
			currency === Currencies.EUR ?
				1 :
				(expenseData?.fuelEuExchangeRate ?? 1);

		const initialRows = (
			(expenseData?.fuelEuCalculationRows ?? []) as FuelEuRow[]
		).map((row) => ({
			...row,
			contractAmount:
			(row.apply ? row.localAmount : 0) *
			initialFx,
		}));

		setSavedSnapshot(
			serializeSnapshot({
				overrideAmount:
				expenseData?.overrideAmount == null ?
					null :
					Number(expenseData.overrideAmount),
				fuelEuExchangeRate: expenseData?.fuelEuExchangeRate ?? null,
				rows: initialRows,
				attachments: (expenseData?.attachments ?? []).map((attachment) => ({
					name: attachment.name,
					attachmentId: attachment.id,
				})),
			}),
		);
	}, [
		expenseData?.id,
		expenseData?.overrideAmount,
		expenseData?.fuelEuExchangeRate,
		expenseData?.fuelEuCalculationRows,
		expenseData?.attachments,
		currency,
	]);

	useEffect(() => {
		setOverrideAmount(
			expenseData?.overrideAmount == null ?
				null :
				Number(expenseData.overrideAmount),
		);
	}, [expenseData?.id, expenseData?.overrideAmount]);

	useEffect(() => {
		setLocalRows(
			(expenseData?.fuelEuCalculationRows ?? []) as FuelEuRow[],
		);
	}, [expenseData?.id, expenseData?.fuelEuCalculationRows]);

	useEffect(() => {
		setFuelEuExchangeRate(expenseData?.fuelEuExchangeRate ?? null);
	}, [expenseData?.id, expenseData?.fuelEuExchangeRate]);

	useEffect(() => {
		const updateDefaultFiles = async () => {
			setLoadingAttachments(true);
			if (expenseData == null || expenseData.attachments?.length === 0) {
				setLoadingAttachments(false);

				return;
			}

			const expenseFiles = await asyncMap(expenseData.attachments ?? [], async (attachment) => {
				const file: File & {
					uid?: string;
					attachmentId?: number;
					deleteable?: boolean;
				} = await getFileFromAttachment(attachment);
				file.uid = `${file.name}-${file.lastModified}`;
				file.attachmentId = attachment.id;

				return {
					file: file as File & {
						uid: string;
						attachmentId: number;
						deleteable?: boolean;
					},
					url: window.URL.createObjectURL(file),
					deleteable: true,
				};
			});
			onSetUploaded(expenseFiles);
			setLoadingAttachments(false);
		};

		updateDefaultFiles();
	}, [expenseData, onSetUploaded]);

	useEffect(() => {
		if (!open) {
			onResetUploaded();
		}
	}, [open, onResetUploaded]);

	const uploadedFiles = Array.isArray(uploaded) ? uploaded : uploaded ? [uploaded] : [];

	const currentSnapshot = serializeSnapshot({
		overrideAmount,
		fuelEuExchangeRate,
		rows: calculatedRows,
		attachments: uploadedFiles.map((u) => {
			const file = u.file as UploadedAttachmentFile;

			return {
				name: file.name,
				attachmentId: file.attachmentId,
			};
		}),
	});

	const hasPendingChanges =
		!loadingAttachments &&
		savedSnapshot != null &&
		currentSnapshot !== savedSnapshot;

	const onSave = async () => {
		if (
			expenseData?.id == null ||
		(overrideAmount == null && hasBreakdown && !hasResolvableFx)
		) {
			return;
		}

		setSaving(true);

		try {
			const amount =
				overrideAmount != null ?
					overrideAmount :
					hasBreakdown ?
						Math.abs(totalContractAmount) :
						expenseData.amount;

			await updateVoyageExpense(expenseData.id, {
				fuelEuExchangeRate,
				fuelEuCalculationRows: hasBreakdown ? calculatedRows : undefined,
				overrideAmount,
				amount,
				attachments: uploadedFiles.map((u) => u.file),
			});

			setSavedSnapshot(currentSnapshot);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title="FuelEU Penalty"
			width="70%"
			destroyOnClose
		>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				<Form layout="vertical">
					<div className={styles.overrideFields}>
						<Form.Item label="Manual overwrite">
							<div className={styles.overrideRow}>
								<CurrencyInput
									allowClear
									value={overrideAmount}
									onClear={() => setOverrideAmount(null)}
									onChange={(value) => {
										setOverrideAmount(
											value == null ?
												null :
												Number(value),
										);
									}}
								/>

								{overrideAmount == null && (
									<Typography.Text
										type="secondary"
										className={styles.overrideInfo}
									>
										You can manually overwrite the calculated cost here
									</Typography.Text>
								)}
							</div>
						</Form.Item>
					</div>

					<div className={styles.calculatedTotal}>
						<Form.Item label="Calculated total">
							<CurrencyInput
								value={Number(Math.abs(totalContractAmount).toFixed(2))}
								readOnly
								disabled={overrideAmount != null}
							/>
						</Form.Item>
					</div>

					{hasBreakdown && !isEurContract && (
						<div className={styles.calculationFields}>
							<Form.Item label={`FX rate (EUR → ${currency})`}>
								<NumericInput
									className={styles.fxInput}
									value={fuelEuExchangeRate}
									addonAfter={(
										<span className={styles.fxLabel}>
											1 EUR = {formatNumber(fuelEuExchangeRate ?? 0)} {currency}
										</span>
									)}
									onChange={(value) => {
										setFuelEuExchangeRate(
											value == null ?
												null :
												Number(value),
										);
									}}
								/>
							</Form.Item>
						</div>
					)}
				</Form>
				{!hasBreakdown ?
					(
						<Typography.Paragraph type="secondary">
							This contract was created before the FuelEU breakdown was introduced.
							Open the itinerary and recalculate emissions to generate the breakdown.
						</Typography.Paragraph>
					) :
					(
						<>
							<Table
								rowKey="key"
								dataSource={calculatedRows}
								pagination={false}
								size="small"
								columns={[
									{
										title: 'Port / Passage',
										dataIndex: 'label',
									},
									{
										title: 'Qualifying year',
										dataIndex: 'year',
										align: 'center',
									},
									{
										title: 'Fuel grade',
										dataIndex: 'fuelType',
										align: 'center',
										render: (value?: string | null) => value ?? '',
									},
									{
										title: 'Source',
										dataIndex: 'consumptionSource',
										key: 'consumptionSource',
										align: 'center',
										render: renderConsumptionSource,
									},
									{
										title: 'Consumption',
										dataIndex: 'consumption',
										align: 'right',
										render: (value: number) => `${formatNumber(value)} MT`,
									},
									{
										title: 'Apply',
										dataIndex: 'apply',
										align: 'center',
										render: (_: boolean, row: FuelEuRow) => (
											<Switch
												disabled={
													row.qualifyingPercent === 0 ||
													row.consumption === 0
												}
												checked={
													row.apply &&
													row.qualifyingPercent > 0 &&
													row.consumption > 0
												}
												onChange={(checked) => {
													setLocalRows((prev) => prev.map((r) => (
														r.key === row.key ?
															{
																...r,
																apply: checked,
															} :
															r
													)));
												}}
											/>
										),
									},
									{
										title: 'Qualifying %',
										dataIndex: 'qualifyingPercent',
										align: 'center',
										render: (value?: number) => `${value ?? 0}%`,
									},
									{

										title: 'Local amount (EUR)',
										dataIndex: 'localAmount',
										align: 'right',
										render: (value: number, row: FuelEuRow) => (
											formatCurrency(
												Math.abs(row.apply ? value : 0),
												Currencies.EUR,
											)
										),
									},
									{
										title: 'Contract amount',
										dataIndex: 'contractAmount',
										align: 'right',
										render: (value: number) => (
											formatCurrency(Math.abs(value), currency)
										),
									},
								]}
								rowClassName={(row) => (
									row.consumption === 0 || row.qualifyingPercent === 0 ?
										styles.disabledRow :
										''
								)}
								summary={() => (
									<Table.Summary.Row>
										<Table.Summary.Cell index={0}>
											<strong>Total</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={1} />
										<Table.Summary.Cell index={2} />
										<Table.Summary.Cell index={3} />
										<Table.Summary.Cell index={4} align="right">
											<strong>{formatNumber(totalConsumption)} MT</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={5} />
										<Table.Summary.Cell index={6} />
										<Table.Summary.Cell index={7} align="right">
											<strong>
												{formatCurrency(Math.abs(totalLocalAmount), Currencies.EUR)}
											</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={8} align="right">
											<strong>
												{formatCurrency(Math.abs(totalContractAmount), currency)}
											</strong>
										</Table.Summary.Cell>
									</Table.Summary.Row>
								)}
							/>
						</>
					)
				}
				<Typography.Title level={5}>Attachments</Typography.Title>
				{loadingAttachments ? (
					<LoadingIndicator />

				) : (
					<div className={styles.attachmentsInner}>
						<InvoiceAttachment
							files={uploaded}
							uploadProps={uploadProps}
							onRemoveFile={onRemoveUpload}
						/>
					</div>
				)}
				<Space style={{ justifyContent: 'flex-end', width: '100%' }}>
					<Button onClick={onClose}>
						Close
					</Button>
					<Button
						type="primary"
						onClick={onSave}
						loading={saving}
						disabled={
							!hasPendingChanges ||
							(
								overrideAmount == null &&
								hasBreakdown &&
								!hasResolvableFx
							)
						}
					>
						Save Changes
					</Button>
				</Space>
			</Space>
		</Drawer >
	);
};

export default FuelEuExpenseDrawer;